import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { callsSomeoneIsIn, presentIds, PRESENCE_FRESH_MS } from '../utils/liveRoomUi';
import { lessonOfCall } from '../utils/incomingCall';

export interface IncomingCall {
  roomId: string;
  conversationId: string;
  initiatorUid: string;
  initiatorName: string;
  /** The lesson the room is on, when the call is a lesson. */
  bookingId?: string;
}

/**
 * Watches for video call rooms the current user is part of, and in which
 * someone else is present right now, so the "On the call" pill shows while
 * they are in and clears when they leave. A call outside a lesson shows only
 * to the side that did not start it; a lesson's shows to either side. Presence is the room's participants with `active` true, which the
 * signalling adapter sets on join and clears on leave.
 *
 * Rooms are excluded when:
 *   - status === 'ended'  (normal path: VideoCallModal sets this on End)
 *   - nobody but this user is active in them with a fresh check-in, which also
 *     covers abnormal exits where status was never set to ended
 *
 * Never by the room's age: a pair's room is permanent (rule 9), made once for
 * their first lesson, so a 4-hour age cut hid every later lesson's banner, and
 * the room's own bookingId is that first lesson's. The lesson is the one the
 * present person's own entry names (lessonOfCall; item 3, 4 Nov).
 */
export function useIncomingCalls(): Array<IncomingCall & { presentUid: string }> {
  const { currentUser } = useAuth();
  const [calls, setCalls] = useState<IncomingCall[]>([]);
  const [present, setPresent] = useState<Record<string, string[]>>({});
  // Each room's present entries' lessons, by person: the lesson the call is now.
  const [entryLesson, setEntryLesson] = useState<Record<string, Record<string, string | null>>>({});

  useEffect(() => {
    // Signed out: the hook returns none whatever these hold.
    if (!currentUser?.uid) return;

    const uid = currentUser.uid;
    // array-contains on a single field needs no composite index.
    // Status and recency filtering happen client-side to keep the query simple.
    const q = query(
      collection(db, 'webrtc_rooms'),
      where('allParticipantUids', 'array-contains', uid),
    );

    return onSnapshot(
      q,
      (snap) => {
        const incoming: IncomingCall[] = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          const status: string = data.status ?? 'ended';
          if (status === 'ended') return;
          // A lesson's room shows to whichever side is not in it, whoever started it
          // (decision 72); a call this user placed outside a lesson is theirs, not incoming.
          const bookingId: string | undefined =
            data.bookingId ?? (d.id.startsWith('call-') ? d.id.slice(5) : undefined);
          if (data.initiatorUid === uid && !bookingId) return;

          // A lesson's room carries its bookingId (a pair's permanent room,
          // rule 9; older rooms used "call-{bookingId}"), read above.
          incoming.push({
            roomId: d.id,
            conversationId: data.conversationId ?? d.id.replace(/^conv-/, ''),
            initiatorUid: data.initiatorUid ?? '',
            initiatorName: data.initiatorName ?? 'Someone',
            bookingId,
          });
        });
        setCalls(incoming);
      },
      () => setCalls([]),
    );
  }, [currentUser?.uid]);

  // Who is in each of those rooms now: active, with a recent heartbeat (the
  // room writes one every 30 seconds), read again on a timer so a closed
  // tab's entry goes stale without another snapshot.
  const roomKey = calls.map((c) => c.roomId).sort().join('|');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!roomKey) return;
    const id = setInterval(() => setTick((t) => t + 1), PRESENCE_FRESH_MS / 3);
    return () => clearInterval(id);
  }, [roomKey]);
  useEffect(() => {
    // No rooms: nothing is read from these (the calls are none), so nothing is cleared.
    if (!roomKey) return;
    const ms = (v: unknown) => (v && typeof (v as Timestamp).toMillis === 'function' ? (v as Timestamp).toMillis() : null);
    const offs = roomKey.split('|').map((roomId) =>
      onSnapshot(
        query(collection(db, 'webrtc_rooms', roomId, 'participants'), where('active', '==', true)),
        (snap) => {
          setPresent((p) => ({ ...p, [roomId]: presentIds(snap.docs.map((d) => ({
            id: d.id, active: d.data().active === true, lastSeenAtMs: ms(d.data().lastSeenAt), joinedAtMs: ms(d.data().joinedAt),
          })), Date.now()) }));
          setEntryLesson((l) => ({ ...l, [roomId]: Object.fromEntries(snap.docs.map((d) => [d.id, typeof d.data().bookingId === 'string' ? d.data().bookingId as string : null])) }));
        },
        () => setPresent((p) => ({ ...p, [roomId]: [] })),
      ));
    return () => { offs.forEach((off) => off()); };
  }, [roomKey, tick]);

  return currentUser
    ? callsSomeoneIsIn(calls, present, currentUser.uid).map((c) => ({ ...c, bookingId: lessonOfCall(c.bookingId, entryLesson[c.roomId]?.[c.presentUid]) }))
    : [];
}
